import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { io } from "socket.io-client";
import { useAuth } from "../context/authContext";
import { clearSession } from "../api/session";
import { API_CONFIG } from "../utils/constants";

/**
 * Mantiene un socket mientras la pestaña está abierta.
 * La presencia se cae al cerrar o recargar, no cuando el token sigue vigente.
 */
export default function ActivityTracker() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const socketRef = useRef(null);
  const pathRef = useRef(location.pathname);

  useEffect(() => {
    pathRef.current = location.pathname;
    if (!socketRef.current?.connected) return;
    socketRef.current.emit("nav", {
      path: location.pathname,
      focused: document.visibilityState === "visible",
    });
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let socket;
    let beat;

    const connect = () => {
      if (socket) socket.disconnect();
      const token = localStorage.getItem("token");
      if (!token) return;

      socket = io(`${API_CONFIG.SOCKET_URL}/presence`, {
        auth: { token },
        transports: ["websocket", "polling"],
      });
      socketRef.current = socket;

      socket.on("connect", () => {
        socket.emit("nav", {
          path: pathRef.current,
          focused: document.visibilityState === "visible",
        });
      });

      socket.on("auth:revoke", () => {
        clearSession();
        if (!window.location.pathname.startsWith("/login")) {
          window.location.assign("/login");
        }
      });
    };

    connect();

    beat = setInterval(() => {
      if (!socketRef.current?.connected) return;
      socketRef.current.emit("beat", {
        focused: document.visibilityState === "visible",
      });
    }, 1000);

    const onToken = () => connect();
    const onVisible = () => {
      if (!socketRef.current?.connected) return;
      socketRef.current.emit("beat", {
        focused: document.visibilityState === "visible",
      });
    };

    window.addEventListener("bremar-token", onToken);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(beat);
      window.removeEventListener("bremar-token", onToken);
      document.removeEventListener("visibilitychange", onVisible);
      socket?.disconnect();
      socketRef.current = null;
    };
  }, [isAuthenticated]);

  return null;
}
